import { CountryCodeMap } from '@commenda/kakashi/src/components/CountryInput/countryCodeMap';
import type {
  FormComponent,
  FormDSL,
  FormModule,
  FormStep,
} from '@commenda/types';
import { Country } from '@prisma/client';
import { State } from 'country-state-city';
import { useMemo } from 'react';
import { readPartnershipStructure } from '../../form-builder/components/wrappers/Ownership/copy';
import {
  formatOwnershipValue,
  isOwnershipValue,
} from '../../form-builder/components/wrappers/Ownership/format';
import { RESOLVED_LABEL_PREFIX } from '../../form-builder/constants';
import { formatNumberForDisplay } from '../../form-builder/utils/number-display';

export interface ParsedField {
  fieldId: string;
  label: string;
  value: unknown;
  component: FormComponent;
}

export interface ParsedModule {
  module: FormModule;
  fields: ParsedField[];
}

export interface ParsedStep {
  step: FormStep;
  modules: ParsedModule[];
}

export interface UseSubmissionParserReturn {
  parsedSteps: ParsedStep[];
  formatValue: (value: unknown, fieldId?: string) => string;
}

const isAddressInput = (value: unknown): boolean => {
  if (
    typeof value !== 'object' ||
    value === null ||
    Array.isArray(value) ||
    value instanceof File
  ) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  const addressKeys = [
    'addressLine1',
    'addressLine2',
    'postalCode',
    'city',
    'state',
    'country',
  ];
  return addressKeys.some((key) => key in obj);
};

const isFileUpload = (value: unknown): boolean => {
  if (
    typeof value !== 'object' ||
    value === null ||
    Array.isArray(value) ||
    value instanceof File
  ) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  const fileKeys = ['fileName', 'fileUrl', 'documentType'];
  return fileKeys.some((key) => key in obj);
};

const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '') return true;
  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !(value instanceof File)
  ) {
    const obj = value as Record<string, unknown>;
    return !Object.values(obj).some(
      (v) => v !== null && v !== undefined && v !== ''
    );
  }
  return false;
};

// Convert country code to name
const getCountryName = (countryCode: string): string => {
  return CountryCodeMap[countryCode as Country] || countryCode;
};

// Convert state code to name
const getStateName = (stateCode: string, countryCode?: string): string => {
  // If country code is provided, try to get state name directly
  if (countryCode) {
    const state = State.getStateByCodeAndCountry(stateCode, countryCode);
    if (state?.name) {
      return state.name;
    }
  }

  // If no country or state not found, try common countries (US first, then others)
  const commonCountries = ['US', 'CA', 'IN', 'GB', 'AU'];
  for (const country of commonCountries) {
    const state = State.getStateByCodeAndCountry(stateCode, country);
    if (state?.name) {
      return state.name;
    }
  }

  // Fallback to the raw code if no match found
  return stateCode;
};

/**
 * Extract resolved display labels stored by AutoDetectField under __label__ keys.
 * Collects labels from both top-level submission data and repeatable module instances.
 */
const extractResolvedLabels = (
  submissionData: Record<string, unknown>
): Record<string, string> => {
  const labels: Record<string, string> = {};

  if (!submissionData) return labels;

  for (const [key, value] of Object.entries(submissionData)) {
    if (key.startsWith(RESOLVED_LABEL_PREFIX) && typeof value === 'string') {
      labels[key.slice(RESOLVED_LABEL_PREFIX.length)] = value;
    }

    // Also extract from repeatable module instance arrays
    if (Array.isArray(value)) {
      value.forEach((instance, index) => {
        if (typeof instance === 'object' && instance !== null) {
          for (const [iKey, iValue] of Object.entries(
            instance as Record<string, unknown>
          )) {
            if (
              iKey.startsWith(RESOLVED_LABEL_PREFIX) &&
              typeof iValue === 'string'
            ) {
              const baseFieldId = iKey.slice(RESOLVED_LABEL_PREFIX.length);
              labels[`${key}.${index}.${baseFieldId}`] = iValue;
            }
          }
        }
      });
    }
  }

  return labels;
};

const buildComponentMap = (formDSL: FormDSL): Record<string, FormComponent> => {
  const componentMap: Record<string, FormComponent> = {};

  formDSL?.steps?.forEach((step) => {
    step?.modules?.forEach((module) => {
      module?.components?.forEach((component) => {
        if (component?.fieldId) {
          componentMap[component?.fieldId] = component;
        }
      });
    });
  });

  return componentMap;
};

// Helper to format a single value from DynamicFields (when field definitions aren't available)
const formatDynamicFieldsValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  // Handle arrays - join with comma
  if (Array.isArray(value)) {
    return value.length > 0 ? value.map((v) => String(v)).join(', ') : '—';
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return String(value);
};

// Helper to format DynamicFields data object as readable key-value pairs
const formatDynamicFieldsObject = (
  obj: Record<string, unknown>,
  component: FormComponent
): string => {
  // Try to get field definitions from static config
  const fieldsSource = (
    component.props as
      | {
          fieldsSource?: {
            type: string;
            fields?: Array<{
              key: string;
              label: string;
              options?: Array<{ value: string; label: string }>;
            }>;
          };
        }
      | undefined
  )?.fieldsSource;

  const staticFields =
    fieldsSource?.type === 'static' && fieldsSource.fields
      ? fieldsSource.fields
      : [];

  // Build a map of key -> field definition
  const fieldDefMap: Record<
    string,
    { label: string; options?: Array<{ value: string; label: string }> }
  > = {};
  for (const def of staticFields) {
    fieldDefMap[def.key] = { label: def.label, options: def.options };
  }

  const entries = Object.entries(obj).filter(
    ([, v]) => v !== null && v !== undefined && v !== ''
  );

  if (entries.length === 0) return '—';

  // Format each entry with proper label
  const parts = entries.map(([key, val]) => {
    const fieldDef = fieldDefMap[key];
    const label =
      fieldDef?.label ||
      key
        .replace(/([A-Z])/g, ' $1')
        .replace(/[_-]/g, ' ')
        .replace(/^\s/, '')
        .split(' ')
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ')
        .trim();

    // Format the value - handle options lookup for selects
    let formattedValue: string;
    if (fieldDef?.options && fieldDef.options.length > 0) {
      if (Array.isArray(val)) {
        const labels = val.map((v) => {
          const opt = fieldDef.options!.find(
            (o) => String(o.value) === String(v)
          );
          return opt?.label || String(v);
        });
        formattedValue = labels.join(', ');
      } else {
        const opt = fieldDef.options.find(
          (o) => String(o.value) === String(val)
        );
        formattedValue = opt?.label || String(val);
      }
    } else {
      formattedValue = formatDynamicFieldsValue(val);
    }

    return `${label}: ${formattedValue}`;
  });

  return parts.join(', ');
};

const formatValue = (
  value: unknown,
  fieldId: string | undefined,
  componentMap: Record<string, FormComponent>,
  resolvedLabels: Record<string, string> = {}
): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  // Check for a pre-resolved display label (stored by AutoDetectField)
  if (fieldId && resolvedLabels[fieldId]) {
    return resolvedLabels[fieldId];
  }

  // For repeatable modules, fieldId is "saveKey.index.fieldId" - use base fieldId for componentMap lookup
  const baseFieldId = fieldId?.includes('.')
    ? fieldId.split('.').pop()
    : fieldId;

  if (baseFieldId && componentMap[baseFieldId]) {
    const component = componentMap[baseFieldId];

    if (component.component === 'BankAccount') {
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        const bankValue = value as {
          selectedId?: string | null;
          displayLabel?: string | null;
        };
        if (bankValue.displayLabel) return bankValue.displayLabel;
        if (bankValue.selectedId)
          return `Bank account ···${bankValue.selectedId.slice(-8)}`;
      }
      return '—';
    }

    // Handle DynamicFields component - format the nested object
    if (component.component === 'DynamicFields') {
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        return formatDynamicFieldsObject(
          value as Record<string, unknown>,
          component
        );
      }
    }

    // Check if this is a CheckboxGroup component
    if (component.component === 'CheckboxGroup') {
      // Handle CheckboxGroup: value is an object like { "option1": true, "option2": true }
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        const obj = value as Record<string, boolean>;
        const selectedLabels: string[] = [];

        // Check if component has options
        if (
          component.options &&
          Array.isArray(component.options) &&
          component.options.length > 0
        ) {
          Object.entries(obj).forEach(([optionValue, isSelected]) => {
            if (isSelected === true) {
              // Normalize values for comparison - handle null/undefined/empty strings
              const normalizedOptionValue =
                optionValue != null ? String(optionValue).trim() : '';

              if (normalizedOptionValue !== '') {
                const option = component.options?.find((opt) => {
                  // Handle null/undefined option values
                  if (opt.value == null) {
                    return normalizedOptionValue === '';
                  }
                  // Normalize and compare option values
                  const normalizedOptValue = String(opt.value).trim();
                  return normalizedOptValue === normalizedOptionValue;
                });

                if (option) {
                  // Use label if available, otherwise fallback to the option value
                  const displayLabel =
                    option.label?.trim() || String(option.value);
                  if (displayLabel) {
                    selectedLabels.push(displayLabel);
                  }
                } else {
                  // Fallback: if option not found, show the raw value
                  selectedLabels.push(normalizedOptionValue);
                }
              }
            }
          });
        } else {
          // If no options defined, show the raw selected values
          Object.entries(obj).forEach(([optionValue, isSelected]) => {
            if (isSelected === true && optionValue) {
              selectedLabels.push(String(optionValue));
            }
          });
        }

        return selectedLabels.length > 0 ? selectedLabels.join(', ') : '—';
      }
    }

    // Handle CountryInput: convert country code to name
    if (component.component === 'CountryInput') {
      return getCountryName(String(value));
    }

    if (
      component.component === 'NumberInput' ||
      (component.component === 'AutoDetectField' && typeof value === 'number')
    ) {
      const formatted = formatNumberForDisplay(value, component.props);
      if (formatted !== null) return formatted;
    }

    // Handle Location: unwrap the nested address object
    if (component.component === 'Location') {
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        const locObj = value as Record<string, unknown>;
        const addressData = locObj.address as
          | Record<string, unknown>
          | undefined;
        if (addressData && isAddressInput(addressData)) {
          return formatValue(
            addressData,
            undefined,
            componentMap,
            resolvedLabels
          );
        }
      }
    }

    // Handle other components with options (RadioGroup, Select)
    if (
      component.options &&
      Array.isArray(component.options) &&
      component.options.length > 0
    ) {
      if (
        component.component === 'RadioGroup' ||
        component.component === 'Select'
      ) {
        const option = component.options.find(
          (opt) => String(opt.value) === String(value)
        );
        if (option && option.label) {
          return option.label;
        }
      }
    }
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'object') {
    if (value instanceof File) {
      return value.name;
    }

    if (Array.isArray(value)) {
      return value.length > 0
        ? value
            .map((v) => formatValue(v, undefined, componentMap, resolvedLabels))
            .join(', ')
        : '—';
    }

    const obj = value as Record<string, unknown>;

    // Ownership node: summarise as an owner list, not a raw object dump.
    if (isOwnershipValue(obj)) {
      const node = baseFieldId ? componentMap?.[baseFieldId] : undefined;
      return formatOwnershipValue(
        value,
        readPartnershipStructure(
          (node as { props?: Record<string, unknown> } | undefined)?.props
        )
      );
    }

    // Handle FileUpload: return only fileName
    if (isFileUpload(obj)) {
      const fileName = obj.fileName;
      if (fileName === null || fileName === undefined || fileName === '') {
        return '—';
      }
      return String(fileName);
    }

    // Handle AddressInput: return comma-separated values with country and state names
    if (isAddressInput(obj)) {
      const addressParts: string[] = [];
      const countryCode = obj.country as string | undefined;

      // Add address fields in order
      for (const field of [
        'addressLine1',
        'addressLine2',
        'city',
        'state',
        'postalCode',
      ]) {
        const fieldValue = obj[field];
        if (
          fieldValue !== null &&
          fieldValue !== undefined &&
          fieldValue !== ''
        ) {
          if (field === 'state') {
            // Always try to convert state code to name
            addressParts.push(getStateName(String(fieldValue), countryCode));
          } else {
            addressParts.push(String(fieldValue));
          }
        }
      }

      // Add country name at the end
      if (countryCode) {
        addressParts.push(getCountryName(countryCode));
      }

      return addressParts.length > 0 ? addressParts.join(', ') : '—';
    }

    // Fallback: Try to find CheckboxGroup component by component name
    // This handles cases where the component wasn't found by fieldId
    // Check if this looks like a CheckboxGroup value (object with boolean values)
    const allValuesAreBooleans = Object.values(obj).every(
      (v) => typeof v === 'boolean'
    );
    if (allValuesAreBooleans && Object.keys(obj).length > 0) {
      // Search for CheckboxGroup components by component name/type
      let foundComponent: FormComponent | undefined;
      for (const comp of Object.values(componentMap)) {
        // Identify by component name/type
        if (comp.component === 'CheckboxGroup' && comp.options) {
          // Check if any of the keys in obj match the component's options
          const hasMatchingOption = Object.keys(obj).some((key) =>
            comp.options?.some((opt) => String(opt.value) === String(key))
          );
          if (hasMatchingOption) {
            foundComponent = comp;
            break;
          }
        }
      }

      if (foundComponent && foundComponent.options) {
        const selectedLabels: string[] = [];
        Object.entries(obj).forEach(([optionValue, isSelected]) => {
          if (isSelected === true) {
            const normalizedOptionValue =
              optionValue != null ? String(optionValue).trim() : '';
            if (normalizedOptionValue !== '') {
              const option = foundComponent.options?.find((opt) => {
                if (opt.value == null) {
                  return normalizedOptionValue === '';
                }
                const normalizedOptValue = String(opt.value).trim();
                return normalizedOptValue === normalizedOptionValue;
              });

              if (option) {
                const displayLabel =
                  option.label?.trim() || String(option.value);
                if (displayLabel) {
                  selectedLabels.push(displayLabel);
                }
              } else {
                selectedLabels.push(normalizedOptionValue);
              }
            }
          }
        });
        if (selectedLabels.length > 0) {
          return selectedLabels.join(', ');
        }
      }

      // If no component found by name but it's a boolean object, show the selected keys
      const selectedKeys = Object.entries(obj)
        .filter(([, v]) => v === true)
        .map(([k]) => k);
      if (selectedKeys.length > 0) {
        return selectedKeys.join(', ');
      }
    }

    // Default object formatting for other types
    const parts = Object.entries(obj)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => {
        const formattedKey = k
          .replace(/([A-Z])/g, ' $1')
          .replace(/[_-]/g, ' ')
          .trim();
        return `${formattedKey}: ${formatValue(v, undefined, componentMap, resolvedLabels)}`;
      });
    return parts.length > 0 ? parts.join(', ') : '—';
  }

  return String(value);
};

const parseFormSubmission = (
  formDSL: FormDSL,
  submissionData: Record<string, unknown>,
  componentMap: Record<string, FormComponent>
): ParsedStep[] => {
  const parsedSteps: ParsedStep[] = [];

  // Parse steps → modules → components
  formDSL?.steps
    ?.sort((a, b) => a.order - b.order)
    .forEach((step) => {
      const parsedModules: ParsedModule[] = [];

      step?.modules
        ?.sort((a, b) => a.order - b.order)
        .forEach((module) => {
          const repeatable = module.repeatable as
            | { enabled: boolean }
            | undefined;
          const saveKey = module.saveKey || module.id;

          if (repeatable?.enabled) {
            // Repeatable module: data is submissionData[saveKey] = [{}, {}, ...]
            const instances = submissionData[saveKey] as
              | Record<string, unknown>[]
              | undefined;
            const arr = Array.isArray(instances) ? instances : [];

            arr.forEach((instanceData, instanceIndex) => {
              const instanceFields: ParsedField[] = [];

              module?.components
                ?.sort((a, b) => a.order - b.order)
                .forEach((component) => {
                  if (component.fieldId) {
                    // Special handling for DynamicFields in repeatable modules
                    if (component.component === 'DynamicFields') {
                      const prefix = component.fieldId + '.';
                      const dynamicFieldsData: Record<string, unknown> = {};

                      // Collect all keys that start with this prefix from instance data
                      Object.keys(instanceData ?? {}).forEach((key) => {
                        if (key.startsWith(prefix)) {
                          const subKey = key.slice(prefix.length);
                          dynamicFieldsData[subKey] = (instanceData ?? {})[key];
                        }
                      });

                      // Also check for direct keys matching fieldId.subKey pattern
                      if (Object.keys(dynamicFieldsData).length === 0) {
                        // In some cases, DynamicFields might store nested data directly
                        const directValue = (instanceData ?? {})[
                          component.fieldId
                        ];
                        if (
                          directValue &&
                          typeof directValue === 'object' &&
                          !Array.isArray(directValue)
                        ) {
                          Object.assign(
                            dynamicFieldsData,
                            directValue as Record<string, unknown>
                          );
                        }
                      }

                      if (Object.keys(dynamicFieldsData).length > 0) {
                        instanceFields.push({
                          fieldId: `${saveKey}.${instanceIndex}.${component.fieldId}`,
                          label: component?.label || component?.fieldId,
                          value: dynamicFieldsData,
                          component,
                        });
                      }
                    } else {
                      const value = (instanceData ?? {})[component.fieldId];

                      if (!isEmptyValue(value)) {
                        instanceFields.push({
                          fieldId: `${saveKey}.${instanceIndex}.${component.fieldId}`,
                          label: component?.label || component?.fieldId,
                          value,
                          component,
                        });
                      }
                    }
                  }
                });

              if (instanceFields.length > 0) {
                parsedModules.push({
                  module: {
                    ...module,
                    name:
                      arr.length > 1
                        ? `${module.name || module.id} ${instanceIndex + 1}`
                        : module.name,
                  },
                  fields: instanceFields,
                });
              }
            });
          } else {
            // Non-repeatable module: flat field lookup
            const fields: ParsedField[] = [];

            module?.components
              ?.sort((a, b) => a.order - b.order)
              .forEach((component) => {
                if (component.fieldId) {
                  // Special handling for DynamicFields component
                  // DynamicFields stores data as {fieldId}.{key} in flat structure
                  if (component.component === 'DynamicFields') {
                    const prefix = component.fieldId + '.';
                    const dynamicFieldsData: Record<string, unknown> = {};

                    // Collect all keys that start with this prefix
                    Object.keys(submissionData).forEach((key) => {
                      if (key.startsWith(prefix)) {
                        const subKey = key.slice(prefix.length);
                        dynamicFieldsData[subKey] = submissionData[key];
                      }
                    });

                    // Only add if there's data
                    if (Object.keys(dynamicFieldsData).length > 0) {
                      fields.push({
                        fieldId: component.fieldId,
                        label: component?.label || component?.fieldId,
                        value: dynamicFieldsData,
                        component,
                      });
                    }
                  } else {
                    const value = submissionData[component?.fieldId];

                    if (!isEmptyValue(value)) {
                      fields.push({
                        fieldId: component?.fieldId,
                        label: component?.label || component?.fieldId,
                        value,
                        component,
                      });
                    }
                  }
                }
              });

            if (fields.length > 0) {
              parsedModules.push({
                module,
                fields,
              });
            }
          }
        });

      if (parsedModules.length > 0) {
        parsedSteps.push({
          step,
          modules: parsedModules,
        });
      }
    });

  return parsedSteps;
};

export function useSubmissionParser(
  formDSL: FormDSL,
  submissionData: Record<string, unknown>
): UseSubmissionParserReturn {
  const componentMap = useMemo(() => buildComponentMap(formDSL), [formDSL]);

  const resolvedLabels = useMemo(
    () => extractResolvedLabels(submissionData),
    [submissionData]
  );

  const parsedSteps = useMemo(
    () => parseFormSubmission(formDSL, submissionData, componentMap),
    [formDSL, submissionData, componentMap]
  );

  const formatValueBound = useMemo(
    () => (value: unknown, fieldId?: string) =>
      formatValue(value, fieldId, componentMap, resolvedLabels),
    [componentMap, resolvedLabels]
  );

  return {
    parsedSteps,
    formatValue: formatValueBound,
  };
}
